import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { useSettings } from "../App";
import {
  Plus, Trash2, Edit, Eye, Printer, Search, X, Wrench
} from "lucide-react";

const STATUS_MAP = {
  draft: { label: "مسودة", color: "bg-gray-100 text-gray-700" },
  sent: { label: "مرسل", color: "bg-blue-100 text-blue-700" },
  accepted: { label: "مقبول", color: "bg-green-100 text-green-700" },
  rejected: { label: "مرفوض", color: "bg-red-100 text-red-700" },
};

export default function ServiceQuotations() {
  const { token } = useAuth();
  const { settings } = useSettings();
  const [quotes, setQuotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showView, setShowView] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [editingQuote, setEditingQuote] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const printRef = useRef();

  const [form, setForm] = useState({
    customer_name: "",
    customer_phone: "",
    customer_id: "",
    items: [{ product_name: "", price: 0, is_manual: true }],
    notes: "",
    validity_days: 30,
  });

  useEffect(() => {
    fetchQuotes();
    fetchCustomers();
    fetchProducts();
  }, []);

  const fetchQuotes = async () => {
    try {
      const res = await axios.get(`${API}/service-quotations`);
      setQuotes(res.data);
    } catch (e) {
      toast.error("خطأ في تحميل عروض أسعار الخدمات");
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API}/customers`);
      setCustomers(res.data);
    } catch (e) {}
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products`);
      setProducts(res.data.filter(p => p.product_type === "service"));
    } catch (e) {}
  };

  const resetForm = () => {
    setForm({
      customer_name: "",
      customer_phone: "",
      customer_id: "",
      items: [{ product_name: "", price: 0, is_manual: true }],
      notes: "",
      validity_days: 30,
    });
    setEditingQuote(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.items.length === 0 || form.items.every(i => !i.product_name)) {
      toast.error("أضف خدمة واحدة على الأقل");
      return;
    }
    try {
      const payload = {
        customer_name: form.customer_name || "عميل نقدي",
        customer_phone: form.customer_phone,
        customer_id: form.customer_id || null,
        items: form.items.filter(i => i.product_name),
        notes: form.notes,
        validity_days: form.validity_days,
      };
      if (editingQuote) {
        await axios.put(`${API}/service-quotations/${editingQuote.id}`, payload);
        toast.success("تم تحديث عرض أسعار الخدمات");
      } else {
        await axios.post(`${API}/service-quotations`, payload);
        toast.success("تم إنشاء عرض أسعار الخدمات");
      }
      fetchQuotes();
      setShowDialog(false);
      resetForm();
    } catch (e) {
      toast.error("حدث خطأ");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("هل تريد حذف هذا العرض؟")) return;
    try {
      await axios.delete(`${API}/service-quotations/${id}`);
      toast.success("تم الحذف");
      fetchQuotes();
    } catch (e) {
      toast.error("خطأ في الحذف");
    }
  };

  const handleEdit = (quote) => {
    setEditingQuote(quote);
    setForm({
      customer_name: quote.customer_name,
      customer_phone: quote.customer_phone || "",
      customer_id: quote.customer_id || "",
      items: quote.items.length > 0 ? quote.items : [{ product_name: "", price: 0, is_manual: true }],
      notes: quote.notes || "",
      validity_days: quote.validity_days || 30,
    });
    setShowDialog(true);
  };

  const handleView = (quote) => {
    setSelectedQuote(quote);
    setShowView(true);
  };

  const addItem = () => {
    setForm({ ...form, items: [...form.items, { product_name: "", price: 0, is_manual: true }] });
  };

  const removeItem = (index) => {
    const newItems = form.items.filter((_, i) => i !== index);
    setForm({ ...form, items: newItems.length > 0 ? newItems : [{ product_name: "", price: 0, is_manual: true }] });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...form.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setForm({ ...form, items: newItems });
  };

  const selectService = (index, productId) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      const newItems = [...form.items];
      newItems[index] = { product_id: product.id, product_name: product.name, price: product.price, is_manual: false };
      setForm({ ...form, items: newItems });
    }
  };

  const handlePrint = () => {
    const content = printRef.current;
    const win = window.open("", "", "width=800,height=900");
    win.document.write(`<html dir="rtl"><head><title>طباعة</title>
      <link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Arabic:wght@300;400;500;600;700&display=swap" rel="stylesheet">
      <style>body{margin:0;padding:20px;font-family:'IBM Plex Sans Arabic',sans-serif}@media print{body{padding:0}}</style>
    </head><body>${content.innerHTML}</body></html>`);
    win.document.close();
    setTimeout(() => { win.print(); win.close(); }, 500);
  };

  const formatDate = (d) => {
    if (!d) return "-";
    return new Date(d).toLocaleDateString("ar-SA");
  };

  const formatCurrency = (v) => {
    return new Intl.NumberFormat("ar-SA", { style: "currency", currency: settings?.currency || "SAR" }).format(v || 0);
  };

  const filteredQuotes = quotes.filter(q =>
    q.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    q.quote_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div></div>;

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Wrench className="w-6 h-6 text-emerald-600" />
            أسعار الخدمات
          </h1>
          <p className="text-gray-500 text-sm mt-1">عروض أسعار الخدمات (بدون إجمالي)</p>
        </div>
        <button onClick={() => { resetForm(); setShowDialog(true); }} className="bg-emerald-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-emerald-700 transition-colors">
          <Plus className="w-4 h-4" /> عرض سعر جديد
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input type="text" placeholder="بحث بالاسم أو رقم العرض..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pr-10 pl-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-right font-medium text-gray-600">رقم العرض</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">العميل</th>
                <th className="px-4 py-3 text-center font-medium text-gray-600">عدد الخدمات</th>
                <th className="px-4 py-3 text-center font-medium text-gray-600">الحالة</th>
                <th className="px-4 py-3 text-center font-medium text-gray-600">التاريخ</th>
                <th className="px-4 py-3 text-center font-medium text-gray-600">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {filteredQuotes.map((q) => (
                <tr key={q.id} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs">{q.quote_number}</td>
                  <td className="px-4 py-3">{q.customer_name}</td>
                  <td className="px-4 py-3 text-center">{q.items?.length || 0}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${(STATUS_MAP[q.status] || STATUS_MAP.draft).color}`}>
                      {(STATUS_MAP[q.status] || STATUS_MAP.draft).label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center text-xs">{formatDate(q.created_at)}</td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => handleView(q)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"><Eye className="w-4 h-4" /></button>
                      <button onClick={() => handleEdit(q)} className="p-1.5 text-amber-600 hover:bg-amber-50 rounded"><Edit className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(q.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredQuotes.length === 0 && (
                <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-400">لا توجد عروض أسعار خدمات</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Dialog */}
      {showDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold">{editingQuote ? "تعديل عرض أسعار الخدمات" : "عرض أسعار خدمات جديد"}</h2>
              <button onClick={() => { setShowDialog(false); resetForm(); }} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Customer */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">اسم العميل</label>
                  <input type="text" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500" placeholder="عميل نقدي" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">رقم الهاتف</label>
                  <input type="text" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500" />
                </div>
              </div>

              {/* Select from existing customers */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">اختيار من العملاء المسجلين</label>
                <select onChange={(e) => {
                  const c = customers.find(c => c.id === e.target.value);
                  if (c) setForm({ ...form, customer_id: c.id, customer_name: c.name, customer_phone: c.phone || "" });
                }} className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500">
                  <option value="">-- اختر عميل --</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>

              {/* Items */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">الخدمات</label>
                  <button type="button" onClick={addItem} className="text-emerald-600 text-sm hover:underline flex items-center gap-1"><Plus className="w-3 h-3" /> إضافة خدمة</button>
                </div>
                <div className="space-y-2">
                  {form.items.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <select onChange={(e) => {
                          if (e.target.value === "__manual__") {
                            updateItem(index, "is_manual", true);
                            updateItem(index, "product_name", "");
                            updateItem(index, "price", 0);
                          } else {
                            selectService(index, e.target.value);
                          }
                        }} className="w-full border rounded px-2 py-1 text-sm mb-1">
                          <option value="__manual__">-- إدخال يدوي --</option>
                          {products.map(p => <option key={p.id} value={p.id}>{p.name} - {formatCurrency(p.price)}</option>)}
                        </select>
                        {item.is_manual && (
                          <input type="text" value={item.product_name} onChange={(e) => updateItem(index, "product_name", e.target.value)} placeholder="اسم الخدمة" className="w-full border rounded px-2 py-1 text-sm" />
                        )}
                      </div>
                      <div className="w-28">
                        <input type="number" value={item.price} onChange={(e) => updateItem(index, "price", parseFloat(e.target.value) || 0)} placeholder="السعر" className="w-full border rounded px-2 py-1 text-sm" />
                      </div>
                      <button type="button" onClick={() => removeItem(index)} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes & Validity */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ملاحظات</label>
                  <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500" rows="2"></textarea>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">صلاحية العرض (أيام)</label>
                  <input type="number" value={form.validity_days} onChange={(e) => setForm({ ...form, validity_days: parseInt(e.target.value) || 30 })} className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500" />
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <button type="submit" className="flex-1 bg-emerald-600 text-white py-2 rounded-lg hover:bg-emerald-700 transition-colors">
                  {editingQuote ? "تحديث" : "إنشاء عرض السعر"}
                </button>
                <button type="button" onClick={() => { setShowDialog(false); resetForm(); }} className="px-4 py-2 border rounded-lg hover:bg-gray-50">إلغاء</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View/Print Dialog */}
      {showView && selectedQuote && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
              <h2 className="text-lg font-bold">عرض أسعار الخدمات</h2>
              <div className="flex items-center gap-2">
                <button onClick={handlePrint} className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700"><Printer className="w-4 h-4" /> طباعة</button>
                <button onClick={() => setShowView(false)} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
              </div>
            </div>

            {/* A4 Template */}
            <div className="p-2 sm:p-6 flex justify-center overflow-hidden">
              <div style={{ width: "794px", transformOrigin: "top center" }} className="invoice-print-wrapper">
                <div ref={printRef} dir="rtl" className="bg-white shadow-lg w-full" style={{ maxWidth: "794px", minHeight: "600px", padding: "40px", fontFamily: "'IBM Plex Sans Arabic', sans-serif", fontSize: "14px", lineHeight: "1.8" }}>

                  {/* Title */}
                  <div style={{ textAlign: "center", marginBottom: "20px" }}>
                    <h2 style={{ fontSize: "28px", fontWeight: "700", color: settings?.primary_color || "#0F5132" }}>عرض أسعار خدمات</h2>
                  </div>

                  {/* Header */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "30px", paddingBottom: "20px", borderBottom: `3px solid ${settings?.primary_color || "#0F5132"}` }}>
                    <div style={{ flex: "1" }}>
                      {settings?.show_logo && settings?.company_logo && (
                        <img src={settings.company_logo} alt="شعار الشركة" style={{ height: "70px", objectFit: "contain", marginBottom: "12px" }} onError={(e) => (e.target.style.display = "none")} />
                      )}
                      <h1 style={{ fontSize: "22px", fontWeight: "700", color: settings?.primary_color || "#0F5132", marginBottom: "6px" }}>{settings?.company_name}</h1>
                      {settings?.company_address && <p style={{ fontSize: "13px", color: "#6b7280", margin: "2px 0" }}>{settings.company_address}</p>}
                      {settings?.company_phone && <p style={{ fontSize: "13px", color: "#6b7280", margin: "2px 0", direction: "ltr", textAlign: "right" }}>{settings.company_phone}</p>}
                      {settings?.company_cr_number && <p style={{ fontSize: "13px", color: "#6b7280", margin: "2px 0" }}>س.ت: {settings.company_cr_number}</p>}
                    </div>
                    <div style={{ textAlign: "center", minWidth: "200px" }}>
                      <table style={{ fontSize: "13px", margin: "0 auto" }}>
                        <tbody>
                          <tr><td style={{ color: "#9ca3af", paddingLeft: "12px" }}>رقم العرض:</td><td style={{ fontWeight: "600" }}>{selectedQuote.quote_number}</td></tr>
                          <tr><td style={{ color: "#9ca3af", paddingLeft: "12px" }}>التاريخ:</td><td style={{ fontWeight: "600" }}>{formatDate(selectedQuote.created_at)}</td></tr>
                          <tr><td style={{ color: "#9ca3af", paddingLeft: "12px" }}>صالح حتى:</td><td style={{ fontWeight: "600" }}>{formatDate(selectedQuote.valid_until)}</td></tr>
                        </tbody>
                      </table>
                      <span style={{ display: "inline-block", marginTop: "8px", padding: "2px 12px", borderRadius: "9999px", fontSize: "12px", fontWeight: "500", backgroundColor: selectedQuote.status === "accepted" ? "#dcfce7" : selectedQuote.status === "rejected" ? "#fee2e2" : selectedQuote.status === "sent" ? "#dbeafe" : "#f3f4f6", color: selectedQuote.status === "accepted" ? "#16a34a" : selectedQuote.status === "rejected" ? "#dc2626" : selectedQuote.status === "sent" ? "#2563eb" : "#374151" }}>
                        {(STATUS_MAP[selectedQuote.status] || STATUS_MAP.draft).label}
                      </span>
                    </div>
                  </div>

                  {/* Customer */}
                  <div style={{ marginBottom: "24px", padding: "16px 20px", borderRadius: "8px", backgroundColor: `${settings?.primary_color || "#0F5132"}08`, border: `1px solid ${settings?.primary_color || "#0F5132"}20` }}>
                    <p style={{ fontSize: "11px", color: "#9ca3af", marginBottom: "4px" }}>معلومات العميل</p>
                    <p style={{ fontSize: "16px", fontWeight: "600" }}>{selectedQuote.customer_name}</p>
                    {selectedQuote.customer_phone && <p style={{ fontSize: "13px", color: "#6b7280", direction: "ltr", textAlign: "right" }}>{selectedQuote.customer_phone}</p>}
                  </div>

                  {/* Services Table - NO TOTAL */}
                  <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "24px" }}>
                    <thead>
                      <tr style={{ backgroundColor: settings?.primary_color || "#0F5132", color: "white" }}>
                        <th style={{ padding: "10px 14px", textAlign: "right", fontSize: "13px", fontWeight: "600", borderRadius: "0 6px 0 0" }}>م</th>
                        <th style={{ padding: "10px 14px", textAlign: "right", fontSize: "13px", fontWeight: "600" }}>الخدمة</th>
                        <th style={{ padding: "10px 14px", textAlign: "left", fontSize: "13px", fontWeight: "600", borderRadius: "6px 0 0 0" }}>السعر</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedQuote.items.map((item, index) => (
                        <tr key={index} style={{ borderBottom: "1px solid #f3f4f6" }}>
                          <td style={{ padding: "12px 14px", fontSize: "13px", color: "#9ca3af" }}>{index + 1}</td>
                          <td style={{ padding: "12px 14px", fontWeight: "500" }}>{item.product_name}</td>
                          <td style={{ padding: "12px 14px", textAlign: "left", fontWeight: "600" }}>{formatCurrency(item.price)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* NO TOTALS SECTION - This is the key difference */}

                  {selectedQuote.notes && (
                    <div style={{ marginBottom: "16px", padding: "12px 16px", backgroundColor: "#fefce8", borderRadius: "8px", fontSize: "13px" }}>
                      <span style={{ fontWeight: "600" }}>ملاحظات: </span>{selectedQuote.notes}
                    </div>
                  )}

                  {settings?.invoice_terms && (
                    <div style={{ marginBottom: "16px", padding: "12px 16px", backgroundColor: "#f9fafb", borderRadius: "8px", fontSize: "12px", color: "#6b7280" }}>
                      <p style={{ fontWeight: "600", color: "#374151", marginBottom: "4px" }}>الشروط والأحكام:</p>
                      <p style={{ whiteSpace: "pre-line" }}>{settings.invoice_terms}</p>
                    </div>
                  )}

                  <div style={{ textAlign: "center", paddingTop: "16px", borderTop: "1px solid #e5e7eb" }}>
                    <p style={{ fontSize: "13px", color: "#9ca3af" }}>{settings?.invoice_footer || "شكراً لتعاملكم معنا"}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
